/*
  Rayvox behaviours, ported from the design board's wire() (design/store-board/board.src.html).

  Sections print the board's markup with the board's data attributes; wire(root) brings those parts to life once
  each. It runs on the whole page at load and again on a section the theme editor re-renders. Light by default:
  things rise in once as they scroll into view and numbers count up; only the Voice Light, equalisers, waveforms
  and marquees loop. Reduced motion shows the finished state at once.
*/

export const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
export const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** Runs fn(k) for k from 0 to 1 over ms (eased); with reduced motion it jumps to the end. Returns a stop function. */
export function tween(ms, fn, done) {
  if (reduce) {
    fn(1);
    done?.();
    return () => {};
  }
  const t0 = performance.now();
  let raf = requestAnimationFrame(function step(now) {
    const t = Math.min(1, (now - t0) / ms);
    fn(easeOut(t));
    if (t < 1) raf = requestAnimationFrame(step);
    else done?.();
  });
  return () => cancelAnimationFrame(raf);
}

const once = (el, key) => {
  if (el.dataset[key]) return false;
  el.dataset[key] = '1';
  return true;
};

const runCounts = (el) =>
  el.querySelectorAll('[data-count]').forEach((c) => {
    const to = +c.dataset.count;
    const dec = +(c.dataset.dec || 0);
    const pre = c.dataset.pre || '';
    const suf = c.dataset.suf || '';
    tween(1600, (k) => {
      c.textContent = pre + (to * k).toFixed(dec) + suf;
    });
  });

const reveal = (el) => {
  el.classList.remove('rv-below');
  el.classList.add('is-in');
  runCounts(el);
  el.dispatchEvent(new CustomEvent('rv:in'));
};

const io = new IntersectionObserver(
  (entries) =>
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      reveal(e.target);
    }),
  { threshold: 0.18 }
);

// Only what is below the screen when this script runs waits to rise in: rv-below hides it (rayvox-theme.css) until
// it scrolls into view. Whatever is already in view, or above it, is never hidden, so a slow or failed script can't
// leave a heading on screen invisible. All are measured together, in one layout.
let waiting = [];
const settle = () => {
  const els = waiting;
  waiting = [];
  els.filter((el) => el.getBoundingClientRect().top >= innerHeight).forEach((el) => el.classList.add('rv-below'));
  els.forEach((el) => io.observe(el));
};

/*
  Pause for moving things (WCAG 2.2.2): the moving rows, the announcement bar, the hero's photo stack, the Pro promo's
  set-ups and the muted clips. pauser(el, what) gives el a pause button, the round play/pause of the board's Seen
  Backstage (`kind` picks a variant in rayvox-theme.css), kept out of sight until it has keyboard focus or el is
  paused, when it shows Play so the state is clear. Unless `tap` is false, a tap or click on el (only on the parts
  matching `tapIn`, if given), not on a link or button inside it, pauses it too, until the next tap. It also holds
  still while keyboard focus is inside, and (unless `mouse` is false) while the mouse is over it. It returns
  { still(), set(on) }, still() saying whether el should hold still right now, and tells el `rv:pause` (detail: still
  or not) whenever that changes. With reduced motion nothing moves, so the behaviours never call it.
*/
const ICONS = {
  pause: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="4" width="5" height="16" rx="1"/><rect x="14" y="4" width="5" height="16" rx="1"/></svg>',
  play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 3.5 19 12 4 20.5z"/></svg>',
};
function pauser(el, what, { place = (b) => el.append(b), kind = '', tap = true, tapIn = '', mouse = true } = {}) {
  if (el.rvPause) return el.rvPause;
  let on = false;
  let over = false;
  let focus = false;
  let was = false;
  const btn = Object.assign(document.createElement('button'), { type: 'button', className: `rv-pause${kind ? ` rv-pause--${kind}` : ''}` });
  const still = () => on || over || focus;
  const tell = () => {
    if (still() === was) return;
    was = still();
    el.dispatchEvent(new CustomEvent('rv:pause', { detail: was }));
  };
  const set = (v) => {
    on = v;
    btn.innerHTML = on ? ICONS.play : ICONS.pause;
    btn.setAttribute('aria-label', `${on ? 'Play' : 'Pause'} ${what}`);
    btn.toggleAttribute('data-paused', on);
    el.toggleAttribute('data-paused', on);
    tell();
  };
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    set(!on);
  });
  if (tap)
    el.addEventListener('click', (e) => {
      if (tapIn && !e.target.closest(tapIn)) return;
      if (!e.target.closest('a, button, input, select, textarea, label, summary, [role="button"]')) set(!on);
    });
  if (mouse) {
    el.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && ((over = true), tell()));
    el.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && ((over = false), tell()));
  }
  // Focus on the pause button itself doesn't hold el still: the button is there to say what pressing it will do.
  el.addEventListener('focusin', (e) => {
    focus = e.target !== btn && e.target.matches(':focus-visible');
    tell();
  });
  el.addEventListener('focusout', (e) => !el.contains(e.relatedTarget) && ((focus = false), tell()));
  set(false);
  place(btn);
  return (el.rvPause = { still, set });
}

// The Pro promo's pause, for the photos and the 3D kit alike: picking a set-up pauses it there until Play is pressed
// on the button by the kit, and keyboard focus inside holds it while it stays. While the set-ups move on their own,
// their names aren't read out at every change; once paused, a picked one is.
const promoPause = (el) => {
  if (el.rvPause) return el.rvPause;
  const label = el.querySelector('[data-pmodename]');
  const stage = el.querySelector('.rv-promo__stage') || el;
  const p = pauser(el, 'the set-ups', { tap: false, mouse: false, place: (b) => stage.append(b) });
  el.querySelectorAll('[data-pp]').forEach((b) => b.addEventListener('click', () => p.set(true)));
  const live = () => label?.setAttribute('aria-live', p.still() ? 'polite' : 'off');
  el.addEventListener('rv:pause', live);
  live();
  return p;
};

/** Every behaviour, keyed by the attribute that marks it. Each gets the element and runs once per element. */
const behaviours = {
  // Rise in once on scroll; numbers inside count up as it arrives. Reduced motion shows everything at once.
  reveal(el) {
    if (reduce || document.documentElement.classList.contains('shopify-design-mode')) return reveal(el);
    if (!waiting.length) queueMicrotask(settle);
    waiting.push(el);
  },

  // Announcement bar: one line at a time, every 3.5 s, holding still on hover or while focus is inside, and paused
  // by a tap on the bar (beside its link) or its small pause button (pauser). The lines not showing are aria-hidden
  // and inert, so only the one on screen can take focus.
  ann(el) {
    const ms = [...el.querySelectorAll('.rv-ann__msgs > span')];
    if (ms.length < 2 || reduce) return;
    let i = 0;
    const p = pauser(el, 'the announcements', { kind: 'sm' });
    setInterval(() => {
      if (p.still() || document.hidden) return;
      ms[i].classList.remove('is-on');
      ms[i].setAttribute('aria-hidden', 'true');
      ms[i].inert = true;
      i = (i + 1) % ms.length;
      ms[i].classList.add('is-on');
      ms[i].removeAttribute('aria-hidden');
      ms[i].inert = false;
    }, 3500);
  },

  // Moving rows (the partner logos, the Wall of Love; data-pausable names them for the button): CSS moves them and
  // holds them on hover and keyboard focus; this adds the tap on a row and the pause button (pauser), on a bar above
  // the first row. Paused, the section carries data-paused, which stops the rows (rayvox-theme.css).
  pausable(el) {
    const first = el.querySelector('.rv-marquee');
    if (reduce || !first) return;
    pauser(el, el.dataset.pausable || 'the moving row', {
      tapIn: '.rv-marquee',
      mouse: false,
      place: (b) => {
        const bar = Object.assign(document.createElement('div'), { className: 'rv-pausebar' });
        bar.append(b);
        first.before(bar);
      },
    });
  },

  // A row you swipe (data-scrollrow names it: the About photo row on phones, the Wall of Love's rows with reduced
  // motion): while it scrolls sideways and holds nothing a keyboard can reach, it takes focus itself, so the arrow
  // keys scroll it (WCAG 2.1.1). Checked again whenever its size changes.
  scrollrow(el) {
    const fit = () => {
      const scrolls = /auto|scroll/.test(getComputedStyle(el).overflowX) && el.scrollWidth > el.clientWidth + 1;
      const reach = [...el.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].some((x) => x.tabIndex >= 0 && !x.closest('[inert]'));
      if (scrolls && !reach) {
        el.tabIndex = 0;
        el.setAttribute('role', 'region');
        el.setAttribute('aria-label', el.dataset.scrollrow);
      } else ['tabindex', 'role', 'aria-label'].forEach((a) => el.removeAttribute(a));
    };
    fit();
    new ResizeObserver(fit).observe(el);
  },

  // Header (sections/rayvox-header.liquid): menus open on hover or click and close on Escape, a click outside, or
  // leaving them; the phone menu is a drawer under the bar, with rows that open to show their links.
  hd(el) {
    const items = [...el.querySelectorAll('[data-hd-item]')];
    const panels = new Map([...el.querySelectorAll('[data-hd-panel]')].map((p) => [p.dataset.hdPanel, p]));
    const scrim = el.querySelector('[data-hd-scrim]');
    const bar = el.querySelector('.rv-hd__bar');
    const menuBtn = el.querySelector('[data-hd-menu]');
    const drawer = el.querySelector('[data-hd-drawer]');
    const wide = matchMedia('(min-width: 760px)');
    // Horizon's add-to-bag animation flies the product to this class.
    el.querySelectorAll('.rv-hd__cart').forEach((c) => c.classList.add('header-actions__cart-icon'));
    let openId = null;
    let timer;

    const state = (s) => {
      if (s) el.dataset.open = s;
      else delete el.dataset.open;
      scrim.hidden = !s;
    };
    const itemFor = (id) => items.find((b) => b.dataset.hdItem === id);
    const closePanel = (focusBack) => {
      if (!openId) return;
      const b = itemFor(openId);
      panels.get(openId).hidden = true;
      b?.setAttribute('aria-expanded', 'false');
      if (focusBack) b?.focus();
      openId = null;
      state(null);
    };
    const openPanel = (id) => {
      if (openId === id) return;
      closePanel();
      const b = itemFor(id);
      const p = panels.get(id);
      if (!p || !b) return;
      if (p.classList.contains('rv-drop')) {
        const x = b.getBoundingClientRect().left - el.getBoundingClientRect().left - 24;
        p.style.setProperty('--rv-drop-x', `${Math.max(14, x)}px`);
      }
      p.hidden = false;
      b.setAttribute('aria-expanded', 'true');
      openId = id;
      state('panel');
    };
    const later = (fn, ms) => {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };

    items.forEach((b) => {
      b.addEventListener('click', () => (openId === b.dataset.hdItem ? closePanel() : openPanel(b.dataset.hdItem)));
      b.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') later(() => openPanel(b.dataset.hdItem), openId ? 0 : 120);
      });
    });
    [bar, ...panels.values()].forEach((a) => {
      a.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse' && openId) later(() => closePanel(), 220);
      });
      a.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && a !== bar) clearTimeout(timer);
      });
    });
    bar.querySelectorAll('.rv-nav__icons > *').forEach((b) => b.addEventListener('click', () => closePanel()));
    el.addEventListener('focusout', (e) => {
      if (openId && !el.contains(e.relatedTarget)) closePanel();
    });

    const setDrawer = (open) => {
      drawer.hidden = !open;
      menuBtn.setAttribute('aria-expanded', String(open));
      document.documentElement.toggleAttribute('scroll-lock', open);
      state(open ? 'drawer' : null);
      if (open) drawer.querySelector('a, button')?.focus({ preventScroll: true });
    };
    menuBtn?.addEventListener('click', () => setDrawer(drawer.hidden));
    el.querySelectorAll('[data-hd-row]').forEach((row) =>
      row.addEventListener('click', () => {
        const open = row.getAttribute('aria-expanded') !== 'true';
        row.setAttribute('aria-expanded', String(open));
        document.getElementById(row.getAttribute('aria-controls')).hidden = !open;
      })
    );

    scrim.addEventListener('click', () => {
      closePanel();
      if (!drawer.hidden) setDrawer(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (openId) closePanel(true);
      else if (!drawer.hidden) {
        setDrawer(false);
        menuBtn.focus();
      }
    });
    wide.addEventListener('change', () => {
      closePanel();
      if (!drawer.hidden) setDrawer(false);
    });
    // Where the bar ends, for panels outside the header that drop from it (the search dialog).
    // Horizon scrolls the page inside .page-wrapper from 990px up, and the document below that.
    const scroller = document.querySelector('.page-wrapper');
    const onScroll = () => {
      el.toggleAttribute('data-scrolled', Math.max(scrollY, scroller?.scrollTop || 0) > 8);
      document.documentElement.style.setProperty('--rv-hd-bottom', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
    };
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    onScroll();
  },

  // Seen Backstage (sections/rayvox-seen-backstage.liquid): clips play muted while on screen, one button pauses them
  // all, they also pause while keyboard focus is inside the section, the dots follow the scroll, and a clip opens
  // full size, story style, running on to the next.
  reel(el) {
    const vids = [...el.querySelectorAll('.rv-reel__media video')];
    const clips = [...el.querySelectorAll('[data-clip]')];
    const btn = el.querySelector('[data-playall]');
    const dots = [...el.querySelectorAll('.rv-pillctl__dots i')];
    const row = el.querySelector('[data-reel-row]');
    let paused = reduce;
    let held = false;
    const seen = new Set();
    const vio = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) seen.add(e.target);
          else seen.delete(e.target);
          if (paused || held) return;
          if (e.isIntersecting) e.target.play().catch(() => {});
          else e.target.pause();
        }),
      { threshold: 0.4 }
    );
    vids.forEach((v) => vio.observe(v));
    // Moving clips hold still while keyboard focus is inside (WCAG 2.2.2), and carry on when it leaves.
    el.addEventListener('focusin', (e) => {
      if (held || !e.target.matches(':focus-visible')) return;
      held = true;
      vids.forEach((v) => v.pause());
    });
    el.addEventListener('focusout', (e) => {
      if (!held || el.contains(e.relatedTarget)) return;
      held = false;
      if (!paused) seen.forEach((v) => v.play().catch(() => {}));
    });
    if (btn) {
      const paint = () => {
        btn.querySelector('[data-ico-pause]').hidden = paused;
        btn.querySelector('[data-ico-play]').hidden = !paused;
        btn.setAttribute('aria-label', paused ? 'Play the clips' : 'Pause the clips');
      };
      btn.addEventListener('click', () => {
        paused = !paused;
        vids.forEach((v) => (paused ? v.pause() : v.play().catch(() => {})));
        paint();
      });
      paint();
    }
    if (row && dots.length)
      row.addEventListener(
        'scroll',
        () => {
          const i = Math.round((row.scrollLeft / Math.max(1, row.scrollWidth - row.clientWidth)) * (dots.length - 1));
          dots.forEach((d, k) => d.classList.toggle('on', k === i));
        },
        { passive: true }
      );

    const vw = el.querySelector('[data-viewer]');
    if (!vw) return;
    const media = vw.querySelector('[data-vmedia]');
    const who = vw.querySelector('[data-vwho]');
    const shop = vw.querySelector('[data-vshop]');
    const bars = [...vw.querySelectorAll('.rv-viewer__bars i')];
    let cur = 0;
    let opener = null;
    let raf = 0;
    const show = (i) => {
      cur = (i + clips.length) % clips.length;
      const c = clips[cur].dataset;
      media.replaceChildren();
      const v = Object.assign(document.createElement('video'), { src: c.src, poster: c.poster, muted: true, loop: true, playsInline: true, autoplay: !reduce });
      v.setAttribute('aria-label', c.label || `${c.name} warming up${c.on ? ` with ${c.on}` : ''}`);
      media.append(v);
      // A rewarded customer video (the Rayvox video offer) keeps its "Ad" chip full size too (terms 18.6).
      if (c.ad != null) media.append(Object.assign(document.createElement('span'), { className: 'vo-chip', textContent: 'Ad' }));
      who.replaceChildren(Object.assign(document.createElement('b'), { textContent: c.name }), c.who);
      shop.hidden = !c.url;
      shop.textContent = c.on ? `Shop ${c.on}` : 'Shop';
      shop.href = c.url || '#';
      bars.forEach((b, k) => (b.querySelector('em').style.width = k < cur ? '100%' : '0'));
      // Each clip runs about two loops (at least 4 seconds), then the next starts; reduced motion stays put.
      const em = bars[cur].querySelector('em');
      const t0 = performance.now();
      cancelAnimationFrame(raf);
      const tick = (now) => {
        const k = Math.min(1, (now - t0) / Math.max(4000, (v.duration || 2) * 2000));
        em.style.width = `${k * 100}%`;
        if (k < 1) raf = requestAnimationFrame(tick);
        else if (!reduce && !vw.hidden) show(cur + 1);
      };
      raf = requestAnimationFrame(tick);
    };
    const close = () => {
      vw.hidden = true;
      media.replaceChildren();
      cancelAnimationFrame(raf);
      document.documentElement.removeAttribute('scroll-lock');
      opener?.focus();
    };
    el.querySelectorAll('[data-open]').forEach((b) =>
      b.addEventListener('click', () => {
        opener = b;
        vw.hidden = false;
        document.documentElement.setAttribute('scroll-lock', '');
        show(+b.dataset.open);
        vw.querySelector('[data-vclose]').focus();
      })
    );
    vw.querySelector('[data-vclose]').addEventListener('click', close);
    vw.querySelector('[data-vprev]').addEventListener('click', () => show(cur - 1));
    vw.querySelector('[data-vnext]').addEventListener('click', () => show(cur + 1));
    vw.addEventListener('click', (e) => e.target === vw && close());
    vw.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') show(cur + 1);
      if (e.key === 'ArrowLeft') show(cur - 1);
      if (e.key === 'Tab') {
        const f = [...vw.querySelectorAll('button, a:not([hidden])')];
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) {
          e.preventDefault();
          f[f.length - 1].focus();
        } else if (!e.shiftKey && i === f.length - 1) {
          e.preventDefault();
          f[0].focus();
        }
      }
    });
  },

  // Pro promo (sections/rayvox-pro-promo.liquid), without the 3D kit: the photos move through the set-ups every few
  // seconds; a point jumps to its set-up and pauses there (promoPause). The photos not showing are aria-hidden.
  pcycle(el) {
    const btns = [...el.querySelectorAll('[data-pp]')];
    const shots = [...el.querySelectorAll('.rv-promo__shot')];
    const label = el.querySelector('[data-pmodename]');
    if (!btns.length) return;
    let cur = 0;
    const go = (i) => {
      cur = (i + btns.length) % btns.length;
      btns.forEach((b, k) => {
        b.classList.toggle('is-on', k === cur);
        b.setAttribute('aria-pressed', String(k === cur));
      });
      shots.forEach((s, k) => {
        s.dataset.shown = '1';
        s.classList.toggle('is-on', k === cur);
        if (k === cur) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
      });
      if (label) label.textContent = btns[cur].querySelector('b').textContent;
    };
    btns.forEach((b, k) => b.addEventListener('click', () => go(k)));
    shots.forEach((s, k) => k && s.setAttribute('aria-hidden', 'true'));
    if (reduce) return;
    const p = promoPause(el);
    let visible = false;
    new IntersectionObserver((es) => es.forEach((e) => (visible = e.isIntersecting))).observe(el);
    setInterval(() => {
      if (visible && !document.hidden && !p.still()) go(cur + 1);
    }, 4500);
  },

  // The 3D Pro promo (data-pauto): the kit's own cycle runs in the 3D scene; this gives it the same pause, which
  // rayvox-3d.js passes to the scene. Until the kit is drawn, only the first photo is read out.
  pauto(el) {
    el.querySelectorAll('.rv-promo__shot').forEach((s, k) => k && s.setAttribute('aria-hidden', 'true'));
    if (!reduce) promoPause(el);
  },

  // Product gallery: a thumbnail shows its photo in the main frame.
  gallery(el) {
    const main = el.querySelector('[data-main]');
    const thumbs = [...el.querySelectorAll('[data-src]')];
    if (!main) return;
    thumbs.forEach((b) =>
      b.addEventListener('click', () => {
        main.srcset = b.dataset.srcset || '';
        main.src = b.dataset.src;
        main.alt = b.dataset.alt || '';
        thumbs.forEach((x) => x.setAttribute('aria-current', String(x === b)));
      })
    );
  },

  // Spare Parts (sections/rayvox-parts.liquid): a sold-out part's Notify Me opens its waitlist signup under the card.
  // After a signup the page reloads with the thanks (or an error) in that box, so it opens again to show it.
  partwait(el) {
    const box = document.getElementById(el.getAttribute('aria-controls'));
    if (!box) return;
    const show = (open) => {
      el.setAttribute('aria-expanded', String(open));
      box.hidden = !open;
    };
    el.addEventListener('click', () => {
      const open = el.getAttribute('aria-expanded') !== 'true';
      show(open);
      if (open) box.querySelector('input[type=email]')?.focus();
    });
    if (box.querySelector('.rv-cdone, .rv-signup__done, .rv-signup__error')) show(true);
  },

  // An alert signup's consent line (snippets/rayvox-signup-form.liquid; board: Consent Line on the Alert Boxes, A). The
  // "Also send me news" box has no name of its own: ticked, it adds `newsletter` to the form's tags as the form is
  // sent, so without this script nobody gets news. After the page comes back with the thanks, it ends the line with
  // "and you'll get our news too" if the box was ticked (remembered for the visit, never in the address).
  alertform(form) {
    const key = `rv-news:${form.id}`;
    const done = form.querySelector('[data-newsdone]');
    if (done) {
      let news = null;
      try {
        news = sessionStorage.getItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {}
      if (news === '1') form.querySelectorAll('[data-newsdone]').forEach((s) => (s.hidden = s.dataset.newsdone !== 'yes'));
      return;
    }
    form.addEventListener('submit', () => {
      const box = form.querySelector('[data-cnews]');
      const tags = form.querySelector('input[name="contact[tags]"]');
      if (!box || !tags) return;
      const list = tags.value
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t && t !== 'newsletter');
      if (box.checked) list.push('newsletter');
      tags.value = list.join(', ');
      try {
        sessionStorage.setItem(key, box.checked ? '1' : '0');
      } catch (e) {}
    });
  },

  // Gift card (sections/rayvox-giftcard.liquid, board: Gift Card, B): the amount picks the variant, and the card
  // fills in with the amount, their name and the message as they're typed. Every recipient field is optional: as the
  // form is sent (the formdata event, which Horizon's product form fires when it reads the form), a blank email drops
  // them all so the code comes to the buyer; with an email, Shopify's gift card flag and the buyer's time zone go too.
  giftb(el) {
    const form = el.querySelector('form');
    const variant = el.querySelector('[data-gvariant]');
    const amts = [...el.querySelectorAll('[data-gv]')];
    const btn = el.querySelector('[data-gbtn]');
    const email = el.querySelector('[data-gemail]');
    const name = el.querySelector('[data-gname]');
    const msg = el.querySelector('[data-gmsgin]');
    const date = el.querySelector('[data-gdate]');
    const set = (sel, v) => el.querySelectorAll(sel).forEach((x) => (x.textContent = v));
    const pick = (b) => {
      amts.forEach((x) => {
        x.setAttribute('aria-checked', String(x === b));
        x.tabIndex = x === b ? 0 : -1;
      });
      if (variant) variant.value = b.dataset.gv;
      set('[data-gamt]', b.dataset.amount);
      if (btn) btn.textContent = (el.dataset.label || 'Add [amount] Gift Card').replace('[amount]', b.dataset.amount);
    };
    amts.forEach((b) => b.addEventListener('click', () => pick(b)));
    el.querySelector('.rv-amounts')?.addEventListener('keydown', (e) => {
      const live = amts.filter((x) => !x.disabled);
      const i = live.indexOf(e.target);
      if (i < 0 || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      const next = live[(i + (['ArrowDown', 'ArrowRight'].includes(e.key) ? 1 : live.length - 1)) % live.length];
      next.focus();
      pick(next);
    });
    name?.addEventListener('input', () => set('[data-gto]', name.value.trim() ? `For ${name.value.trim()}` : ''));
    msg?.addEventListener('input', () => set('[data-gmsg]', msg.value.trim()));
    // Shopify sends a gift card up to 90 days ahead.
    if (date) {
      const day = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const now = new Date();
      date.min = day(now);
      date.max = day(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90));
    }
    if (!form) return;
    // Check the fields before Horizon's own submit handler (which listens on document) sends anything.
    addEventListener(
      'submit',
      (e) => {
        if (e.target !== form) return;
        const bad = [email, date].find((f) => f && f.value && !f.checkValidity());
        if (!bad) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        bad.reportValidity();
      },
      true
    );
    const RECIP = ['properties[Recipient email]', 'properties[Recipient name]', 'properties[Message]', 'properties[Send on]'];
    form.addEventListener('formdata', (e) => {
      const fd = e.formData;
      const to = (fd.get('properties[Recipient email]') || '').toString().trim();
      if (!to) {
        RECIP.forEach((k) => fd.delete(k));
        fd.delete('properties[__shopify_offset]');
        fd.delete('properties[__shopify_send_gift_card_to_recipient]');
        return;
      }
      ['properties[Recipient name]', 'properties[Message]', 'properties[Send on]'].forEach((k) => {
        if (!(fd.get(k) || '').toString().trim()) fd.delete(k);
      });
      fd.set('properties[__shopify_offset]', String(new Date().getTimezoneOffset()));
      if (el.dataset.giftcard === 'true') fd.set('properties[__shopify_send_gift_card_to_recipient]', 'on');
    });
  },

  // Search results (sections/rayvox-search.liquid): the All, Products and Pages and Guides tabs show one group or
  // both. Without JavaScript both groups simply show.
  sres(el) {
    const tabs = [...el.querySelectorAll('[data-stab]')];
    const groups = [...el.querySelectorAll('[data-sgroup]')];
    const show = (t) => {
      tabs.forEach((x) => {
        x.setAttribute('aria-selected', String(x === t));
        x.tabIndex = x === t ? 0 : -1;
      });
      groups.forEach((g) => (g.hidden = t.dataset.stab !== 'all' && g.dataset.sgroup !== t.dataset.stab));
    };
    tabs.forEach((t) => {
      t.tabIndex = t.getAttribute('aria-selected') === 'true' ? 0 : -1;
      t.addEventListener('click', () => !t.disabled && show(t));
    });
    el.querySelector('[role=tablist]')?.addEventListener('keydown', (e) => {
      const live = tabs.filter((x) => !x.disabled);
      const i = live.indexOf(e.target);
      if (i < 0 || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      const next = live[(i + (e.key === 'ArrowRight' ? 1 : live.length - 1)) % live.length];
      next.focus();
      show(next);
    });
  },

  // Reshipment (sections/rayvox-reshipment.liquid): Yes shows the code box, No the way to Contact (arrow keys move
  // between them, as in any radio group). On the way to checkout the code becomes "RS #<order number>", sent as the
  // "Reshipment code" attribute and the order's note. Without JavaScript the form still sends what was typed.
  reship(el) {
    const rs = [...el.querySelectorAll('[data-rs]')];
    const pick = (r, focus) => {
      rs.forEach((x) => {
        x.setAttribute('aria-checked', String(x === r));
        x.tabIndex = x === r ? 0 : -1;
      });
      el.querySelectorAll('[data-rs-yes], [data-rs-no]').forEach((p) => {
        p.hidden = !p.matches(`[data-rs-${r.dataset.rs}]`);
      });
      if (focus) r.focus();
    };
    rs.forEach((r) => {
      r.tabIndex = r.getAttribute('aria-checked') === 'true' ? 0 : -1;
      r.addEventListener('click', () => pick(r));
      r.addEventListener('keydown', (e) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        pick(rs[(rs.indexOf(r) + step + rs.length) % rs.length], true);
      });
    });
    const form = el.querySelector('[data-rs-form]');
    const code = form?.querySelector('[data-rs-code]');
    const err = form?.querySelector('[data-rs-err]');
    if (!form || !code) return;
    const described = code.getAttribute('aria-describedby') || '';
    const flag = (bad) => {
      if (err) err.hidden = !bad;
      if (bad) code.setAttribute('aria-invalid', 'true');
      else code.removeAttribute('aria-invalid');
      code.setAttribute('aria-describedby', bad && err ? `${err.id} ${described}`.trim() : described);
    };
    code.addEventListener('input', () => flag(false));
    form.addEventListener('submit', (e) => {
      const digits = code.value.replace(/\D/g, '');
      if (!digits) {
        e.preventDefault();
        flag(true);
        code.focus();
        return;
      }
      code.value = `RS #${digits}`;
      let note = form.querySelector('input[name="note"]');
      if (!note) {
        note = Object.assign(document.createElement('input'), { type: 'hidden', name: 'note' });
        form.append(note);
      }
      note.value = `Reshipment code: ${code.value}`;
    });
  },

  // Quantity stepper beside Add to Bag.
  qty(el) {
    const input = el.querySelector('input');
    el.querySelectorAll('[data-q]').forEach((b) =>
      b.addEventListener('click', () => {
        input.value = String(Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + +b.dataset.q)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  },

  // One-off or subscribe (sections/rayvox-product.liquid): sets the form's selling_plan, the frequency, the button
  // label, and hides express checkout for subscriptions (it can't take one).
  subopt(el) {
    const form = el.closest('form');
    const radios = [...el.querySelectorAll('.rv-radio')];
    const freq = el.querySelector('[data-freq]');
    const input = el.querySelector('[data-plan-input]');
    const btn = form?.querySelector('[data-mainbtn]');
    const express = form?.querySelector('[data-express]');
    const plans = [...freq.querySelectorAll('[data-plan]')];
    const plan = () => (plans.find((b) => b.getAttribute('aria-pressed') === 'true') || plans[0])?.dataset.plan || '';
    // The sticky bar's button keeps its geo pair (rv-geo-off / rv-geo-only): only the buy words change, so a visitor
    // whose country is placed after a toggle still gets the email wording there.
    const barLabel = document.querySelector('[data-pdpbar-btn] .rv-geo-off');
    const set = (sub) => {
      radios.forEach((r) => r.setAttribute('aria-checked', String((r.dataset.o === 'sub') === sub)));
      freq.hidden = !sub;
      input.value = sub ? plan() : '';
      if (express) express.hidden = sub;
      if (!btn) return;
      const label = sub ? btn.dataset.labelSub : btn.dataset.labelOne;
      btn.textContent = label;
      if (barLabel) barLabel.textContent = label;
    };
    radios.forEach((r) => r.addEventListener('click', () => set(r.dataset.o === 'sub')));
    el.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key) || !e.target.matches('.rv-radio')) return;
      e.preventDefault();
      const i = radios.indexOf(e.target);
      const next = radios[(i + (['ArrowDown', 'ArrowRight'].includes(e.key) ? 1 : radios.length - 1)) % radios.length];
      next.focus();
      next.click();
    });
    plans.forEach((b) =>
      b.addEventListener('click', () => {
        plans.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        input.value = b.dataset.plan;
      })
    );
  },

  // Variant buttons, for products with more than one: set the form's variant, the price and the address. The part
  // picker (Spare Parts, role="radio") also shows the part's own photo, and a sold-out part turns the button into
  // "Sold Out" (board: Simple Product Page, B).
  buyform(el) {
    const input = el.querySelector('[data-variant-input]');
    const btns = [...el.querySelectorAll('[data-variant]')];
    const page = el.closest('[data-page]');
    const radio = btns.some((b) => b.getAttribute('role') === 'radio');
    const main = el.querySelector('[data-mainbtn]');
    const express = el.querySelector('[data-express]');
    const label = main?.dataset.labelOne || 'Add to Bag';
    const pick = (b) => {
      btns.forEach((x) => {
        if (radio) {
          x.setAttribute('aria-checked', String(x === b));
          x.tabIndex = x === b ? 0 : -1;
        } else x.setAttribute('aria-pressed', String(x === b));
      });
      input.value = b.dataset.variant;
      const price = page?.querySelector('[data-price]');
      if (price) price.textContent = b.dataset.price;
      el.dispatchEvent(new CustomEvent('rv:variant', { bubbles: true, detail: { cents: +b.dataset.cents } }));
      const was = page?.querySelector('[data-was]');
      if (was) was.hidden = !b.dataset.was;
      if (b.dataset.ok && main) {
        const ok = b.dataset.ok === 'true';
        main.disabled = !ok;
        main.textContent = ok ? label : 'Sold Out';
        if (express) express.hidden = !ok;
      }
      const img = page?.querySelector('[data-gallery] [data-main]');
      if (img && b.dataset.img) {
        img.src = b.dataset.img;
        img.srcset = b.dataset.srcset || '';
        page.querySelectorAll('[data-gallery] [data-src]').forEach((t) => t.setAttribute('aria-current', String(t.dataset.src === b.dataset.img)));
      }
      const url = new URL(location.href);
      url.searchParams.set('variant', b.dataset.variant);
      history.replaceState(null, '', url);
    };
    btns.forEach((b) => b.addEventListener('click', () => pick(b)));
    if (radio)
      el.addEventListener('keydown', (e) => {
        const i = btns.indexOf(e.target);
        if (i < 0 || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        e.preventDefault();
        const next = btns[(i + (['ArrowDown', 'ArrowRight'].includes(e.key) ? 1 : btns.length - 1)) % btns.length];
        next.focus();
        pick(next);
      });
  },

  // Sticky Add to Bag: slides in once the main button has scrolled up out of view; its button does the main one's job.
  page(el) {
    const bar = document.querySelector('[data-pdpbar]');
    const buy = el.querySelector('[data-mainbtn]') || el.querySelector('#waitlist');
    const geoBox = el.querySelector('[data-geo-buy]');
    if (!bar || !buy) return;
    // For a visitor in a country we don't deliver to yet (html.rv-geo-on), the bar follows the email box instead,
    // unless this product ships there anyway (.rv-geo-sells, the US trial).
    const main = () =>
      geoBox && document.documentElement.classList.contains('rv-geo-on') && !el.closest('.rv-geo-sells') ? geoBox : buy;
    bar.inert = true;
    const io = new IntersectionObserver((es) =>
      es.forEach((e) => {
        if (e.target !== main()) return;
        const up = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-up', up);
        bar.inert = !up;
      })
    );
    io.observe(buy);
    if (geoBox) io.observe(geoBox);
    bar.querySelector('[data-pdpbar-btn]')?.addEventListener('click', () => {
      const m = main();
      if (m.matches('[data-mainbtn]')) m.click();
      else {
        m.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        m.querySelector('input[type=email]')?.focus({ preventScroll: true });
      }
    });
  },

  // Footer: Cookie Preferences (L10) reopens Shopify's own cookie choices. Shopify's banner script sets
  // window.privacyBanner when the banner is on; until then (or without it) the button goes to the privacy policy.
  cookieprefs(el) {
    el.addEventListener('click', () => {
      const banner = window.privacyBanner;
      if (banner && typeof banner.showPreferences === 'function') banner.showPreferences();
      else if (el.dataset.fallback) window.location.href = el.dataset.fallback;
    });
  },

  // A section's own Add to Bag on a product page (the 3D demos): does what the sticky bar's button does. In stock,
  // it presses the buy box's button; sold out, or for a visitor in a country we don't deliver to yet, it takes them
  // to the waitlist or the email box. Off a product page it's a plain link to the product.
  tobuy(el) {
    el.addEventListener('click', (e) => {
      // A button for another product (the Pro kit's demo on the Go's page, say) is a plain link.
      if (new URL(el.href, location.href).pathname !== location.pathname) return;
      const geo =
        document.documentElement.classList.contains('rv-geo-on') &&
        !document.querySelector('.rv-pdp.rv-geo-sells') &&
        document.querySelector('[data-geo-buy]');
      const main = document.querySelector('[data-mainbtn]');
      if (!geo && main) {
        e.preventDefault();
        main.click();
        return;
      }
      const to = geo || document.querySelector('#waitlist');
      if (!to) return;
      e.preventDefault();
      to.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      to.querySelector('input[type=email]')?.focus({ preventScroll: true });
    });
  },

  // Add to Your Order (sections/rayvox-product.liquid; Selleasy's product page add-ons, carried over at go-live): the
  // rows come in a new order on each visit, and the first four show. On Add to Bag the ticked ones go into the bag in
  // one request first (one of each, at the variant the row shows), then they're unticked and Horizon's own add runs
  // for the product, with its quantity, options, subscription and engraving untouched, and refreshes the bag drawer,
  // which by then holds the add-ons too. If an add-on can't go in (sold out since the page loaded, say), the others
  // still do, one at a time, a line under the rows says which couldn't, and the product still goes in.
  // Like engrave(), this captures submit on window to go before Horizon. It must go before engrave() too, which is why
  // it's defined above it (wire() runs behaviours in this order): engrave() adds its fee, then submits the form again,
  // and if this one came after, its own re-submit would make engrave() add a second fee. Going first, this one has
  // unticked the boxes before engrave() runs, so the later submits pass straight through it.
  addons(el) {
    const form = el.closest('form');
    const list = el.querySelector('[data-addon-list]');
    const err = el.querySelector('[data-addon-err]');
    if (!form || !list) return;
    const rows = [...list.querySelectorAll('[data-addon-row]')];
    for (let i = rows.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rows[i], rows[j]] = [rows[j], rows[i]];
    }
    rows.forEach((r, i) => (r.hidden = i >= 4));
    list.append(...rows);
    const say = (msg) => {
      if (err) err.textContent = msg;
    };
    el.addEventListener('change', () => say(''));
    const addUrl = `${(window.Theme?.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
    const post = (items) =>
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((r) => r.ok)
        .catch(() => false);
    let busy = false;
    addEventListener(
      'submit',
      async (e) => {
        if (e.target !== form) return;
        // A second press while the add-ons are on their way waits for them, so the product doesn't go in twice.
        if (busy) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        const ticked = [...el.querySelectorAll('[data-addon-tick]:checked')].filter((t) => !t.closest('[hidden]'));
        if (!ticked.length) return;
        const main = form.querySelector('[data-mainbtn]');
        if (main?.disabled) return;
        // Engraving switched on with no words yet: engrave() asks for them, and nothing goes in.
        const eng = form.querySelector('[data-eng-on]');
        if (eng?.checked && !form.querySelector('[data-eng-text]')?.value.trim()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        busy = true;
        el.setAttribute('aria-busy', 'true');
        say('');
        let failed = [];
        if (!(await post(ticked.map((t) => ({ id: +t.value, quantity: 1 }))))) {
          failed = ticked;
          if (ticked.length > 1) {
            // One of them was refused: add the others one at a time. Any already in the bag now are left alone, in
            // case they went in with the refused request, so nothing is ever added twice.
            failed = [];
            const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null);
            const inBag = new Set((cart?.items || []).map((i) => String(i.variant_id)));
            for (const t of ticked) {
              if (inBag.has(t.value)) continue;
              if (!(await post([{ id: +t.value, quantity: 1 }]))) failed.push(t);
            }
          }
        }
        ticked.forEach((t) => {
          t.checked = false;
          t.dispatchEvent(new Event('change', { bubbles: true }));
        });
        if (failed.length) {
          const names = failed.map((t) => t.dataset.name).filter(Boolean);
          say(`Sorry, ${names.length ? names.join(' and ') : 'an add-on'} couldn’t be added.`);
        }
        el.removeAttribute('aria-busy');
        busy = false;
        form.requestSubmit(main || undefined);
      },
      true
    );
  },

  // Engraving (board: Purchase Options, B): a switch opens the text box and the total adds the fee. On Add to Bag the
  // fee item goes into the bag first, carrying the words, then Horizon's own add runs (which refreshes the drawer).
  // Horizon handles submit with a capturing listener on document, so this one captures on window to go first.
  engrave(el) {
    const on = el.querySelector('[data-eng-on]');
    const field = el.querySelector('[data-eng-field]');
    const text = el.querySelector('[data-eng-text]');
    const form = el.closest('form');
    const page = el.closest('[data-page]') || document;
    const row = page.querySelector('[data-eng-totalrow]');
    const total = page.querySelector('[data-eng-total]');
    if (!on || !text || !form) return;
    const fee = +el.dataset.fee;
    let base = +el.dataset.base;
    const qty = () => Math.max(1, parseInt(form.querySelector('input[name=quantity]')?.value, 10) || 1);
    const money = (cents) =>
      (el.dataset.money || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
        const v = cents / 100;
        if (k === 'amount_no_decimals') return Math.round(v).toLocaleString('en-GB');
        if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',');
        return v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      });
    // The total counts the ticked Add to Your Order boxes too (one of each), and shows once anything is added.
    const ticked = () => [...page.querySelectorAll('[data-addon-tick]:checked')].reduce((t, b) => t + (+b.dataset.price || 0), 0);
    const paint = () => {
      const extra = ticked();
      if (row) row.hidden = !on.checked && !extra;
      if (total) total.textContent = money((base + (on.checked ? fee : 0)) * qty() + extra);
    };
    page.addEventListener('change', (e) => {
      if (e.target.matches && e.target.matches('[data-addon-tick]')) paint();
    });
    // The fee and its Resono share a hidden line item property, a new id for each Add to Bag, so the bag can keep
    // them together (assets/rayvox-bag.js, decision L5). The Resono's copy rides in the form as a hidden input, there
    // only while engraving is on, so Horizon's own add sends it.
    const link = () => {
      let i = form.querySelector('[data-eng-link]');
      if (!i) {
        i = document.createElement('input');
        i.type = 'hidden';
        i.name = 'properties[_engraving_id]';
        i.dataset.engLink = '';
        form.append(i);
      }
      return i;
    };
    on.addEventListener('change', () => {
      field.hidden = !on.checked;
      text.required = on.checked;
      if (!on.checked) form.querySelector('[data-eng-link]')?.remove();
      paint();
      if (on.checked) text.focus();
    });
    text.addEventListener('input', () => text.setCustomValidity(''));
    form.addEventListener('change', (e) => e.target.name === 'quantity' && paint());
    page.addEventListener('rv:variant', (e) => {
      base = e.detail.cents || base;
      paint();
    });
    // Sent from the Engraving page (sections/rayvox-engraving.liquid) with #engrave: switch engraving on, with the
    // words typed there (kept for the visit in sessionStorage, never in the address, and cleared once used).
    if (location.hash === '#engrave' && !on.disabled) {
      let words = '';
      try {
        const s = JSON.parse(sessionStorage.getItem('rv-engrave') || 'null');
        if (s && Date.now() - s.at < 36e5) words = String(s.words || '');
        sessionStorage.removeItem('rv-engrave');
      } catch (e) {}
      on.checked = true;
      field.hidden = false;
      text.required = true;
      if (words) text.value = text.maxLength > 0 ? words.slice(0, text.maxLength) : words;
      paint();
      requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest', behavior: 'instant' }));
    }
    let pass = false;
    addEventListener(
      'submit',
      async (e) => {
        if (e.target !== form || !on.checked || pass) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const words = text.value.trim();
        if (!words) {
          text.setCustomValidity('Type what you’d like engraved, or turn engraving off.');
          text.reportValidity();
          return;
        }
        const id = Math.random().toString(36).slice(2, 10).toUpperCase();
        link().value = id;
        // Horizon's cart_add_url already ends in .js; adding another worked, but only by Shopify's leniency.
        const addUrl = `${(window.Theme?.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: +el.dataset.feeVariant,
                quantity: qty(),
                properties: { Engraving: words, For: el.dataset.for, _engraving_id: id },
              },
            ],
          }),
        }).catch(() => null);
        if (!res?.ok) {
          const msg = (await res?.json().catch(() => null))?.description || 'Engraving couldn’t be added. Please try again.';
          text.setCustomValidity(msg);
          text.reportValidity();
          return;
        }
        pass = true;
        form.requestSubmit(form.querySelector('[data-mainbtn]') || undefined);
        pass = false;
      },
      true
    );
  },

  // Country notice (sections/rayvox-geo-notice.liquid): asks Shopify once per visit which country the visitor is in.
  // In one we don't deliver to yet, html.rv-geo-on shows the notice and swaps product buy boxes for an email box, and
  // signups get tagged deliver-to-<country>. "Shop as UK" turns it off for the visit. Crawlers are left alone.
  geo(el) {
    const html = document.documentElement;
    const list = (el.dataset.countries || '').split(',').filter(Boolean);
    const remember = (v) => {
      try {
        sessionStorage.setItem('rv-geo', v);
      } catch (e) {}
    };
    // Where something ships already (the notice's data-offer-*, the US trial), the card announces it
    // (html.rv-geo-offer), the waitlists show it, and buttons elsewhere that lead to it keep their buy words.
    const offers = (el.dataset.offers || '').split(',').filter(Boolean);
    const o = el.dataset;
    const apply = (code) => {
      const on = !!code && list.includes(code);
      const offer = on && offers.includes(code);
      html.classList.toggle('rv-geo-on', on);
      html.classList.toggle('rv-geo-offer', offer);
      try {
        sessionStorage.setItem('rv-geo-offer', offer ? '1' : '');
      } catch (e) {}
      document.querySelectorAll('[data-geo-offer]').forEach((a) => {
        a.hidden = !offer || location.pathname === o.offerUrl;
        if (a.hidden) return;
        a.href = o.offerUrl;
        a.querySelector('[data-geo-offer-name]').textContent = o.offerName;
        a.querySelector('[data-geo-offer-note]').textContent = o.offerNote;
        const img = a.querySelector('[data-geo-offer-img]');
        if (o.offerImg) img.src = o.offerImg;
        else img.remove();
      });
      // On the offer's own page, it sells even when the store's localization didn't know the country (a US visitor
      // on www): the buy box stays, as it does when the page was made for the US market (.rv-geo-sells).
      if (offer && location.pathname === o.offerUrl) {
        document.querySelectorAll('.rv-pdp, [data-pdpbar]').forEach((p) => p.classList.add('rv-geo-sells'));
      }
      document.querySelectorAll('[data-tobuy]').forEach((b) => {
        b.classList.toggle('rv-geo-sells', offer && new URL(b.href, location.href).pathname === o.offerUrl);
      });
      if (!on) return;
      const name = code === 'US' ? el.dataset.us : el.dataset.eu;
      el.querySelectorAll('[data-geo-heading]').forEach((h) => (h.textContent = h.dataset.template.replace('{country}', name)));
      // The alert boxes name the visitor's own country: "one the day we deliver to Germany" (board: Consent Line).
      let own = name;
      if (code !== 'US') {
        try {
          const n = new Intl.DisplayNames(['en-GB'], { type: 'region' }).of(code);
          if (n && n !== code) own = code === 'NL' ? 'the Netherlands' : n;
        } catch (e) {}
      }
      document.querySelectorAll('[data-geo-country]').forEach((s) => (s.textContent = own));
      document.querySelectorAll('input[name="contact[tags]"]').forEach((i) => {
        i.value = i.value.replace(/(^|,\s*)deliver-to(?=\s*(,|$))/, `$1deliver-to-${code.toLowerCase()}`);
      });
    };
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-geo-uk]')) return;
      remember('off');
      apply('');
    });
    document.addEventListener('submit', (e) => {
      if (e.target.closest('.rv-geo-uk')) remember('off');
    });
    // Seeing the site as another country (for checking): ?geo=US or ?geo=DE holds for the visit; ?geo=GB ends it.
    const forced = new URLSearchParams(location.search).get('geo');
    if (forced) {
      const c = forced.toUpperCase();
      remember(list.includes(c) ? `on:${c}` : 'off');
    }
    let saved = null;
    try {
      saved = sessionStorage.getItem('rv-geo');
    } catch (e) {}
    if (saved) return apply(saved.startsWith('on:') ? saved.slice(3) : '');
    if (/bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent)) return;
    fetch('/browsing_context_suggestions.json?country[enabled]=true')
      .then((r) => r.json())
      .then((d) => {
        const code = d?.detected_values?.country?.handle || '';
        const on = list.includes(code);
        remember(on ? `on:${code}` : 'off');
        apply(on ? code : '');
      })
      .catch(() => {});
  },

  // Reviews: "Write a Review" opens Judge.me's own review form (its link is hidden in the restyled widget). Judge.me
  // loads its form script on the first click if it hasn't yet (it preloads it only when its own link is on screen,
  // and ours is hidden), so when that click only loaded the script, click again once the form is set up.
  writereview(el) {
    el.addEventListener('click', () => {
      const section = el.closest('.rv-reviews');
      const link = section?.querySelector('.jdgm-write-rev-link');
      if (!link) return;
      const form = () => section.querySelector('.jdgm-form-wrapper');
      const isOpen = () => form() && getComputedStyle(form()).display !== 'none';
      const show = () => form()?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      let done = false;
      const retry = () => {
        if (done) return;
        done = true;
        if (!isOpen()) link.click();
        show();
      };
      link.click();
      if (isOpen()) {
        show();
        return;
      }
      document.addEventListener('jdgm.doneSetupReviewForm', () => setTimeout(retry, 0), { once: true });
      setTimeout(() => isOpen() && retry(), 800);
    });
  },

  // How It Works: Studio's straw diagram. It demonstrates itself once when it first appears, then it's yours.
  straw(fig) {
    const input = fig.querySelector('input');
    const sp = fig.querySelector('[data-sp]');
    const ss = fig.querySelector('[data-ss]');
    const sa = fig.querySelector('[data-sa]');
    const mp = fig.querySelector('[data-mp]');
    const mf = fig.querySelector('[data-mf]');
    const st = fig.querySelector('[data-status]');
    const words = (n) => (n < 1 / 3 ? fig.dataset.wide : n < 2 / 3 ? fig.dataset.mid : fig.dataset.narrow);
    let said = '';
    const set = (v) => {
      const n = v / 100;
      const p = 0.2 + 0.75 * n;
      const f = 0.95 - 0.7 * n;
      sp.setAttribute('fill-opacity', (0.06 + 0.34 * p).toFixed(3));
      ss.setAttribute('stroke-width', (18 - 13 * n).toFixed(1));
      sa.setAttribute('opacity', (0.25 + 0.75 * p).toFixed(3));
      mp.style.width = `${Math.round(p * 100)}%`;
      mf.style.width = `${Math.round(f * 100)}%`;
      const w = words(n);
      if (w !== said) {
        said = w;
        st.textContent = w;
        input.setAttribute('aria-valuetext', w);
      }
    };
    let touched = false;
    let stop = null;
    input.addEventListener('input', () => {
      touched = true;
      stop?.();
      set(+input.value);
    });
    const host = fig.closest('[data-reveal]');
    if (fig.hasAttribute('data-demo') && !reduce && host)
      host.addEventListener(
        'rv:in',
        () => {
          const path = [50, 92, 12, 50];
          let seg = 0;
          const next = () => {
            if (touched || seg >= path.length - 1) return;
            const a = path[seg];
            const b = path[seg + 1];
            seg++;
            stop = tween(1100, (k) => {
              const v = a + (b - a) * k;
              input.value = v;
              set(v);
            }, () => setTimeout(next, 250));
          };
          setTimeout(next, 700);
        },
        { once: true }
      );
  },

  // Try-it steps: tap to tick off.
  check(el) {
    el.addEventListener('click', () => el.setAttribute('aria-pressed', String(el.getAttribute('aria-pressed') !== 'true')));
  },

  // Which Resono? (sections/rayvox-compare.liquid): three taps to a match; the match's column lights up in the table.
  quiz(el) {
    const body = el.querySelector('[data-qbody]');
    const data = el.querySelector('[data-quizdata]');
    if (!body || !data) return;
    const QUIZ = JSON.parse(data.textContent);
    const segs = [...el.querySelectorAll('.rv-quiz .rv-segs i')];
    const label = el.querySelector('[data-qcount]');
    const light = (c) => el.querySelectorAll('[data-col]').forEach((x) => x.classList.toggle('col-on', x.dataset.col === c));
    const parse = (s) =>
      Object.fromEntries(
        String(s || '')
          .split(',')
          .map((x) => x.split(':').map((y) => y.trim()))
          .filter(([c, n]) => c && n)
          .map(([c, n]) => [c, +n])
      );
    let step = 0;
    let picks = [];
    const show = (focus) => {
      segs.forEach((s, i) => (s.className = i < step ? 'done' : i === step ? 'now' : ''));
      if (step < QUIZ.length) {
        label.textContent = `Question ${step + 1} of ${QUIZ.length}`;
        light('');
        const wrap = document.createElement('div');
        wrap.className = 'rv-quiz__step';
        const h = Object.assign(document.createElement('h3'), { className: 'rv-quiz__q', textContent: QUIZ[step].q, tabIndex: -1 });
        const opts = Object.assign(document.createElement('div'), { className: 'rv-quiz__opts' });
        QUIZ[step].o.forEach((o, i) => {
          const b = Object.assign(document.createElement('button'), { type: 'button', className: 'rv-quiz__opt', textContent: o.t });
          b.dataset.o = i;
          opts.append(b);
        });
        wrap.append(h, opts);
        if (step) {
          const back = Object.assign(document.createElement('button'), { type: 'button', className: 'rv-linkbtn', textContent: 'Back' });
          back.dataset.back = '';
          wrap.append(back);
        }
        body.replaceChildren(wrap);
        if (focus) h.focus();
        return;
      }
      label.textContent = 'Your Match';
      const score = {};
      picks.forEach((o, i) => Object.entries(parse(QUIZ[i].o[o].s)).forEach(([c, n]) => (score[c] = (score[c] || 0) + n)));
      const cols = [...el.querySelectorAll('template[data-result]')].map((t) => t.dataset.result);
      const best = cols.reduce((a, c) => ((score[c] || 0) > (score[a] || 0) ? c : a), cols[0]);
      const tpl = el.querySelector(`template[data-result="${best}"]`);
      const frag = tpl.content.cloneNode(true);
      const why = frag.querySelector('[data-why]');
      const tick = frag.querySelector('[data-best] svg')?.outerHTML || '';
      picks
        .map((o, i) => QUIZ[i].o[o])
        .filter((o) => parse(o.s)[best])
        .reverse()
        .forEach((o) => {
          const li = document.createElement('li');
          li.innerHTML = tick;
          li.append(`You chose: ${o.t}`);
          why.prepend(li);
        });
      body.replaceChildren(frag);
      window.Rayvox?.wire(body);
      light(best);
      if (focus) body.querySelector('h3')?.setAttribute('tabindex', '-1'), body.querySelector('h3')?.focus();
    };
    body.addEventListener('click', (e) => {
      const o = e.target.closest('[data-o]');
      if (o) {
        picks[step] = +o.dataset.o;
        step++;
        show(true);
        return;
      }
      if (e.target.closest('[data-back]')) {
        step--;
        picks.length = step;
        show(true);
        return;
      }
      if (e.target.closest('[data-restart]')) {
        step = 0;
        picks = [];
        show(true);
      }
    });
    show(false);
  },

  // Judge.me's widget, as pre-rendered: until its own script dresses it, give each review its date and its
  // "Verified Buyer" line (the board's card footer). Dates always read "Aug 2026", also after Judge.me's script has
  // written its own (dd/mm/yyyy) and on each page it loads; the badge is left alone once Judge.me has filled it.
  jdgm(el) {
    const check = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 12 5 5 9-10"/></svg>';
    const words = (el.dataset.hide || '').toLowerCase().split(',').map((w) => w.trim()).filter(Boolean);
    const fill = () =>
      el.querySelectorAll('.jdgm-rev').forEach((r) => {
        if (words.length && words.some((w) => r.textContent.toLowerCase().includes(w))) r.classList.add('rv-rev--hidden');
        r.querySelectorAll('img[data-src]:not([src])').forEach((img) => (img.src = img.dataset.src));
        const t = r.querySelector('.jdgm-rev__timestamp');
        const when = t?.getAttribute('datetime');
        if (when) {
          const text = new Date(when).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
          if (t.textContent !== text) t.textContent = text;
          t.classList.remove('jdgm-spinner');
        }
        const b = r.querySelector('.jdgm-rev__buyer-badge');
        if (b && !b.textContent.trim()) b.innerHTML = r.dataset.verifiedBuyer === 'true' ? `${check}Verified Buyer` : 'Customer';
      });
    fill();
    new MutationObserver(fill).observe(el, { childList: true, subtree: true });
  },

  // Vocalitea's ingredients: tap one to read about it and light up where in the cup you'll taste it.
  tea(stage) {
    const sec = stage.closest('.rv');
    const note = sec.querySelector('[data-tea-note]');
    const sips = [...sec.querySelectorAll('.rv-tea__journey li')];
    const btns = [...stage.querySelectorAll('[data-ing]')];
    btns.forEach((b) =>
      b.addEventListener('click', () => {
        const on = b.getAttribute('aria-pressed') !== 'true';
        btns.forEach((x) => x.setAttribute('aria-pressed', String(x === b && on)));
        const s = +b.dataset.stage;
        sips.forEach((li, i) => li.classList.toggle('is-on', on && i === s));
        if (!on) {
          note.textContent = note.dataset.idle;
          return;
        }
        const name = Object.assign(document.createElement('b'), { textContent: `${b.dataset.name}.` });
        // An ingredient the tasting notes don't name (stage -1, Natural Flavouring) gets no "where" line.
        const where = [];
        if (sips[s]) {
          const line = `${note.dataset.where} ${sips[s].querySelector('b').textContent}`;
          where.push(Object.assign(document.createElement('span'), { textContent: line }));
        }
        note.replaceChildren(name, ` ${b.dataset.text}`, ...where);
      })
    );
  },

  // A muted clip that plays only while it's on screen (and never with reduced motion). A tap on it, or its pause
  // button, stops it until the next tap (pauser).
  autoplay(v) {
    if (reduce) return;
    let seen = false;
    const host = v.parentElement;
    if (host && getComputedStyle(host).position === 'static') host.style.position = 'relative';
    // Named after its clip, so a page of clips doesn't read out a row of identical "Pause the clip" buttons.
    const name = v.getAttribute('aria-label');
    const p = pauser(v, name ? `the clip, ${name}` : 'the clip', { kind: 'clip', mouse: false, place: (b) => v.after(b) });
    const run = () => (seen && !p.still() ? v.play().catch(() => {}) : v.pause());
    v.addEventListener('rv:pause', run);
    new IntersectionObserver((es) => es.forEach((e) => ((seen = e.isIntersecting), run())), { threshold: 0.3 }).observe(v);
  },

  // Photo stack: the top polaroid flicks away every 5 s and goes to the back; it holds still on hover, and is paused
  // by a tap on it or its pause button (pauser). Only the three on top are read out; the ones hidden behind are
  // aria-hidden.
  stack(el) {
    const cards = [...el.querySelectorAll('.rv-polaroid')];
    const shown = () =>
      cards.forEach((c) => (/^[012]$/.test(c.dataset.pos) ? c.removeAttribute('aria-hidden') : c.setAttribute('aria-hidden', 'true')));
    shown();
    if (cards.length < 2 || reduce) return;
    let order = cards.map((_, i) => i);
    const p = pauser(el, 'the photos');
    setInterval(() => {
      if (p.still() || document.hidden) return;
      cards[order[0]].dataset.pos = 'out';
      setTimeout(() => {
        order = [...order.slice(1), order[0]];
        order.forEach((ci, pos) => (cards[ci].dataset.pos = String(pos)));
        shown();
      }, 450);
    }, 5000);
  },

  // Logo in motion (snippets/rayvox-logo-motion.liquid): starts when it comes into view. A looping one keeps circling;
  // one inside the loader is run by the loader instead.
  lgm(el) {
    const ctl = (el.rvLogo = playLogo(el));
    if (el.closest('[data-loader]')) return;
    const looping = el.dataset.loop === '1';
    const vo = new IntersectionObserver(
      (es) => {
        const seen = es.some((e) => e.isIntersecting);
        if (looping) return seen ? ctl.start() : ctl.stop();
        if (!seen) return;
        vo.disconnect();
        ctl.start();
      },
      { threshold: 0.3 }
    );
    vo.observe(el);
  },

  // The loader (sections/rayvox-loader.liquid), once per visit and only when the visit starts on the homepage (the
  // head script decides): the drops circle until the page has loaded (from 0.4 s to 0.7 s after the page was asked
  // for, or at once if this script runs later), form the rings at twice the board's pace (the mark is finished
  // 1.08 s later), and the veil lifts 1.2 s after forming starts, so the whole mark holds for a moment as on the
  // board, then fades in 0.4 s: about 2 seconds in all (decision L18). The stylesheet's failsafe lifts it at 3 s.
  loader(el) {
    const html = document.documentElement;
    const logo = el.querySelector('[data-lgm]');
    if (!html.classList.contains('rv-intro') || !logo) {
      html.classList.remove('rv-intro');
      return;
    }
    try {
      sessionStorage.setItem('rv-intro', '1');
    } catch (e) {}
    const ctl = logo.rvLogo || (logo.rvLogo = playLogo(logo));
    ctl.start();
    let formed = false;
    const lift = () => {
      el.classList.add('is-done');
      setTimeout(() => {
        html.classList.remove('rv-intro');
        ctl.stop();
      }, 450);
    };
    // If a busy page held up the frames, the mark may not be drawn in full at 1.2 s: then the veil waits for it (up
    // to a second more) and holds it 0.15 s, so the finished logo is seen before it fades.
    const lifting = (tries) => {
      if (ctl.finished() || tries > 25) setTimeout(lift, tries ? 150 : 0);
      else setTimeout(lifting, 40, tries + 1);
    };
    const form = () => {
      if (formed) return;
      formed = true;
      ctl.form(2);
      setTimeout(lifting, 1200, 0);
    };
    // performance.now() counts from the start of the page's request, so a slow script doesn't lengthen the wait.
    const whenLoaded = () => setTimeout(form, Math.max(0, 400 - performance.now()));
    if (document.readyState === 'complete') whenLoaded();
    else addEventListener('load', whenLoaded, { once: true });
    setTimeout(form, Math.max(0, 700 - performance.now()));
  },

  // The Rayvox video offer (sections/rayvox-video-offer.liquid, sections/rayvox-video-offer-card.liquid). A piece
  // with a closing time (the card: the end of 7 March 2027, UK time) removes itself once it has passed, for a page
  // a cache kept from before then; Liquid already leaves it out of pages made after.
  vountil(el) {
    const t = Date.parse(el.dataset.vountil);
    if (t && Date.now() >= t) el.remove();
  },

  // The offer page's calculator: tap what you bought and the two amounts add up, one of each product at today's
  // price, half of it rounded down to the penny (terms 10.1) and all of it. The page prints the first product's.
  vocalc(el) {
    const picks = [...el.querySelectorAll('[data-vopick]')];
    const half = el.querySelector('[data-vo50]');
    const all = el.querySelector('[data-vo100]');
    let fmt;
    try {
      fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: el.dataset.currency || 'GBP', currencyDisplay: 'narrowSymbol' });
    } catch (e) {
      fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    }
    const code = el.dataset.code ? ` ${el.dataset.code}` : '';
    const paint = () => {
      const sum = picks.filter((p) => p.getAttribute('aria-pressed') === 'true').reduce((a, p) => a + (+p.dataset.price || 0), 0);
      half.textContent = fmt.format(Math.floor(sum / 2) / 100) + code;
      all.textContent = fmt.format(sum / 100) + code;
    };
    picks.forEach((p) =>
      p.addEventListener('click', () => {
        p.setAttribute('aria-pressed', String(p.getAttribute('aria-pressed') !== 'true'));
        paint();
      })
    );
  },

  // The claim form: What You Bought switches the order fields for the Studio account email (only the shown email is
  // sent), and the form goes in two steps, your order and post, then confirm and sign. Next checks the first step's
  // fields before moving on. Without this script the whole form shows at once and still sends.
  voclaim(form) {
    const done = form.querySelector('.vo-done');
    if (done) {
      done.focus();
      return;
    }
    const kinds = [...form.querySelectorAll('[data-vokind]')];
    const setKind = () => {
      const v = kinds.find((r) => r.checked)?.dataset.vokind || 'order';
      form.querySelectorAll('[data-vofor]').forEach((g) => {
        const on = g.dataset.vofor === v;
        g.hidden = !on;
        g.querySelectorAll('input').forEach((i) => (i.disabled = !on));
      });
    };
    kinds.forEach((r) => r.addEventListener('change', setKind));
    setKind();

    const steps = [...form.querySelectorAll('[data-vostep]')];
    const stepper = form.querySelector('.vo-stepper');
    const lis = stepper ? [...stepper.querySelectorAll('li')] : [];
    const back = form.querySelector('[data-voback]');
    const next = form.querySelector('[data-vonext]');
    const send = form.querySelector('[data-vosend]');
    if (steps.length < 2 || !next || !back || !send) return;
    let k = 1;
    const go = (n, focus) => {
      k = Math.max(1, Math.min(steps.length, n));
      steps.forEach((s) => (s.hidden = +s.dataset.vostep !== k));
      lis.forEach((li, i) => {
        li.classList.toggle('is-done', i + 1 < k);
        if (i + 1 === k) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
      });
      back.hidden = k === 1;
      next.hidden = k === steps.length;
      send.hidden = k !== steps.length;
      if (focus) steps[k - 1].querySelector('input:not([type="hidden"]):not([disabled])')?.focus();
    };
    const forward = () => {
      const bad = [...steps[k - 1].querySelectorAll('input, select, textarea')].find((i) => !i.disabled && !i.checkValidity());
      if (bad) bad.reportValidity();
      else go(k + 1, true);
    };
    next.addEventListener('click', forward);
    back.addEventListener('click', () => go(k - 1, true));
    // Enter in a field moves on a step instead of sending a claim whose later fields are still out of sight.
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || k === steps.length || !e.target.matches('input')) return;
      e.preventDefault();
      forward();
    });
    // If the browser finds a field it can't show, open its step.
    form.addEventListener(
      'invalid',
      (e) => {
        const s = e.target.closest('[data-vostep]');
        if (s?.hidden) go(+s.dataset.vostep);
      },
      true
    );
    if (stepper) stepper.hidden = false;
    go(1);
  },

  // Seen Backstage's "Why Ad?" on a rewarded customer video: opens and closes the line saying why it's an ad.
  vowhy(btn) {
    const pop = document.getElementById(btn.getAttribute('aria-controls'));
    if (!pop) return;
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      pop.hidden = !open;
    });
  },
};

/*
  The logo's motion, as on the board: drops of sound circle the centre on their own ellipses and merge (an SVG
  "goo" filter), glide to the ring centres and each opens into its ring with a spring; the big ring arrives with a
  motion blur and is cut into its C; then the letters rise. A stroked circle with r = width / 2 looks like a filled
  drop, so growing r while thinning the stroke turns a drop into a ring.
*/
const RINGS = [
  [23.2895, 21.71],
  [46.5789, 28.95],
  [81.1841, 36.18],
  [118.947, 43.42],
];
function playLogo(el) {
  const blobs = [...el.querySelectorAll('[data-blobs] circle')];
  const rings = [...el.querySelectorAll('[data-rings] circle')];
  const clip = el.querySelector('[data-clip]');
  const mb = el.querySelector('[data-mb]');
  const wide = !!el.querySelector('.lg-word');
  const C = wide ? 200 : 60;
  const K = wide ? 1.9 : 1;
  const seeds = blobs.map((_, i) => ({
    rx: (20 + i * 9) * K,
    ry: (12 + (i % 3) * 8) * (wide ? 1.15 : 1),
    w: (i % 2 ? -1 : 1) * (1.3 + i * 0.22),
    ph: i * 1.1,
    r: [8, 10, 12, 14, 9, 7][i],
  }));
  const home = [0, 1, 2, 3, 3, 2];
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const spring = (t) => 1 - Math.cos(t * Math.PI * 2.4) * Math.exp(-5.5 * t);
  const clamp = (t) => Math.max(0, Math.min(1, t));
  const OPEN = 850;
  const STEP = 120;
  let loop = el.dataset.loop === '1';
  let t0 = performance.now();
  let formAt = loop ? Infinity : 1500;
  let raf = 0;
  let wordOn = false;
  // pace speeds up the forming (drops to rings) without speeding up the circling: the loader forms at 2x.
  let pace = 1;
  // How far the forming had got at the last frame drawn, in the board's milliseconds.
  let drawn = -Infinity;
  const draw = (t) => {
    const d = (t - formAt) * pace;
    drawn = d;
    blobs.forEach((b, i) => {
      const s = seeds[i];
      const a = s.ph + (s.w * t) / 1000;
      const ox = C + Math.cos(a) * s.rx;
      const oy = 45 + Math.sin(a) * s.ry * (1 + 0.15 * Math.sin(t / 700 + i));
      const e = ease(clamp(d / 1000));
      const x = ox + (RINGS[home[i]][0] - ox) * e;
      const y = oy + (45 - oy) * e;
      const open = clamp((d - OPEN - home[i] * STEP) / 600);
      const br = s.r * (1 + 0.16 * Math.sin(t / 240 + i * 2));
      b.setAttribute('cx', x.toFixed(2));
      b.setAttribute('cy', y.toFixed(2));
      b.setAttribute('r', Math.max(0, br * (1 - open)).toFixed(2));
    });
    rings.forEach((c, i) => {
      const R = RINGS[i][1];
      const k = clamp((d - OPEN - i * STEP) / 950);
      const r0 = seeds[i].r;
      if (k <= 0) {
        c.setAttribute('r', '0');
        c.setAttribute('stroke-width', '0');
        return;
      }
      c.setAttribute('r', Math.max(0.1, r0 / 2 + (R - r0 / 2) * spring(k)).toFixed(2));
      c.setAttribute('stroke-width', (r0 + (3.158 - r0) * ease(k)).toFixed(2));
    });
    const k4 = clamp((d - OPEN - 3 * STEP) / 950);
    mb?.setAttribute('stdDeviation', `${(6 * Math.sin(Math.PI * Math.min(1, k4 * 1.4))).toFixed(2)} 0`);
    clip?.setAttribute('width', (320 - (320 - 138.947) * ease(clamp((k4 - 0.3) / 0.6))).toFixed(2));
    const on = d > OPEN + 900;
    if (on !== wordOn) {
      wordOn = on;
      el.classList.toggle('is-word', on);
    }
  };
  const frame = (now) => {
    const t = now - t0;
    draw(t);
    if (loop || (t - formAt) * pace < OPEN + 2400) raf = requestAnimationFrame(frame);
    else raf = 0;
  };
  const start = () => {
    cancelAnimationFrame(raf);
    loop = el.dataset.loop === '1';
    t0 = performance.now();
    formAt = loop ? Infinity : 1500;
    pace = 1;
    drawn = -Infinity;
    wordOn = false;
    el.classList.remove('is-word');
    // Reduced motion: the finished logo at once (a looping one too, since circling is motion).
    if (reduce) {
      formAt = -1e5;
      draw(0);
      return;
    }
    raf = requestAnimationFrame(frame);
  };
  return {
    start,
    form: (speed = 1) => {
      formAt = performance.now() - t0;
      pace = speed;
      loop = false;
      if (!raf) raf = requestAnimationFrame(frame);
    },
    stop: () => cancelAnimationFrame(raf),
    // The last frame drew the whole mark: the big ring has settled (its C cut finishes a little earlier).
    finished: () => drawn >= OPEN + 3 * STEP + 950,
  };
}

/*
  A section with its own script (the 3D viewers, say) registers its behaviour through window.Rayvox, which works
  whichever script loads first: push a function onto window.RayvoxQueue and it's called with the API once this
  module runs. Importing this file by URL instead would load a second copy, because asset URLs carry a version.
*/
export function define(name, fn) {
  behaviours[name] = fn;
  wire(document);
}

export function wire(root = document) {
  for (const [name, fn] of Object.entries(behaviours)) {
    const attr = `data-${name}`;
    const key = `rvWired${name[0].toUpperCase()}${name.slice(1)}`;
    const els = [...root.querySelectorAll(`[${attr}]`)];
    if (root instanceof Element && root.hasAttribute(attr)) els.unshift(root);
    // One behaviour failing mustn't stop the rest of the page coming to life.
    els.forEach((el) => {
      if (!once(el, key)) return;
      try {
        fn(el);
      } catch (e) {
        console.error(`Rayvox: ${name}`, e);
      }
    });
  }
}

/*
  The cart add-on (snippets/rayvox-cart-addon.liquid): asks Shopify for the first product's complementary products,
  rendered by sections/rayvox-cart-addon.liquid, and shows the first that isn't in the bag. When none of them can be
  offered (most products have no complementary list), it shows up to two from the section's fixed list instead, those
  not in the bag, in the list's order. If the recommendations can't be had at all, the section is asked for on its own,
  for the fixed list. Horizon re-renders the bag after every change, which reconnects the element and asks again.
  Nothing fits, nothing shows.
*/
class RayvoxCartAddon extends HTMLElement {
  connectedCallback() {
    const { product, inCart = '', label, url, formJs } = this.dataset;
    if (!product || !url) return;
    const have = new Set(inCart.split(',').filter(Boolean));
    const get = (u) =>
      fetch(u)
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '');
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    get(`${url}?product_id=${product}&limit=4&intent=complementary&section_id=rayvox-cart-addon`)
      .then((html) => html || get(`${root}?section_id=rayvox-cart-addon`))
      .then((html) => {
        if (!html || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // In a country we don't deliver to yet (html.rv-geo-on), only what ships there can be offered: the country
        // notice's offer (the US trial's Pro v2) where there is one, nothing otherwise.
        const page = document.documentElement;
        const offerId = page.classList.contains('rv-geo-offer') ? document.querySelector('[data-geo]')?.dataset.offerId : '';
        const ships = (el) => !page.classList.contains('rv-geo-on') || (!!offerId && el.dataset.addon === offerId);
        const rows = [...doc.querySelectorAll('[data-addon]')].filter((el) => !have.has(el.dataset.addon) && ships(el));
        let picks = rows.filter((el) => !el.hasAttribute('data-addon-fallback')).slice(0, 1);
        if (!picks.length) {
          const seen = new Set();
          picks = rows
            .filter((el) => el.hasAttribute('data-addon-fallback') && !seen.has(el.dataset.addon) && seen.add(el.dataset.addon))
            .slice(0, 2);
        }
        if (!picks.length) return;
        picks.forEach((pick, i) => {
          pick.hidden = false;
          // One "Pairs Well With Your …" line, on the first row.
          const l = pick.querySelector('[data-addon-label]');
          if (l && i === 0) l.textContent = label;
          else l?.remove();
          pick.querySelectorAll('script').forEach((s) => s.remove());
        });
        if (formJs) import(formJs).catch(() => {});
        this.replaceChildren(...picks);
        wire(this);
      })
      .catch(() => {});
  }
}
if (!customElements.get('rayvox-cart-addon')) customElements.define('rayvox-cart-addon', RayvoxCartAddon);

const api = { define, wire, tween, reduce, easeOut };
window.Rayvox = api;
(window.RayvoxQueue || []).forEach((f) => f(api));
window.RayvoxQueue = { push: (f) => f(api) };

wire(document);
document.addEventListener('shopify:section:load', (e) => wire(e.target));
